import React, { useState } from "react";
import { Formik, Form, Field } from "formik";
import * as Yup from "yup";
import {
  ButtonComp,
  CardComp,
  TextInputComp,
  SelectComp2,
} from "../../../components";
import { PageTitle } from "../modules/PageHeaders";
import {
  useGetBirthdaySettingsQuery,
  useUpdateBirthdaySettingsMutation,
} from "../../../store/admin/automatedBirthdaySlice";
import { toast } from "react-hot-toast";
import TableSkeletonLoader from "../../../components/Common/TableSkeletonLoader";

const BirthdaySettings = () => {
  const { data: settings, isLoading } = useGetBirthdaySettingsQuery();
  const [updateSettings, { isLoading: isUpdating }] =
    useUpdateBirthdaySettingsMutation();

  const validationSchema = Yup.object({
    is_enabled: Yup.boolean(),
    recipients: Yup.string().required("Please select recipients"),
    sending_time: Yup.string().required("Please set a sending time"),
  });

  const handleSubmit = async (values) => {
    try {
      const response = await updateSettings(values).unwrap();
      if (response.status) {
        toast.success("Birthday settings updated successfully");
      } else {
        toast.error(response.message || "Failed to update settings");
      }
    } catch (error) {
      toast.error("An error occurred while updating settings");
    }
  };

  if (isLoading) {
    return (
      <section className="px-3 px-lg-5 min-vh-100 pb-5 mb-5">
        <PageTitle title="Birthday Settings" />
        <CardComp cardClassName="mt-4">
          <TableSkeletonLoader rows={5} columns={1} />
        </CardComp>
      </section>
    );
  }

  const initialValues = {
    is_enabled: settings?.is_enabled ?? false,
    recipients: settings?.recipients ?? "both",
    sending_time: settings?.sending_time ?? "06:00",
    channels: settings?.channels ?? ["email", "in-app", "sms"],
  };

  return (
    <section className="px-3 px-lg-5 min-vh-100 pb-5 mb-5">
      <div className="mt-1">
        <PageTitle title="Birthday Settings" />
      </div>

      <Formik
        initialValues={initialValues}
        validationSchema={validationSchema}
        onSubmit={handleSubmit}
        enableReinitialize
      >
        {({ values, setFieldValue }) => (
          <Form className="mt-4">
            <div className="space-y-4">
              {/* Enable/Disable Automation */}
              <CardComp cardClassName="rounded-4 shadow3 border-0">
                <div className="d-flex align-items-center justify-content-between">
                  <div>
                    <h5 className="fw-bold text-black2 mb-1">
                      Enable Birthday Automation
                    </h5>
                    <p className="text-blacklight mb-0 small">
                      Automatically send birthday messages to users
                    </p>
                  </div>
                  <div className="form-check form-switch">
                    <input
                      className="form-check-input"
                      type="checkbox"
                      role="switch"
                      id="is_enabled"
                      checked={values.is_enabled}
                      onChange={(e) =>
                        setFieldValue("is_enabled", e.target.checked)
                      }
                      style={{
                        width: "3em",
                        height: "1.5em",
                        cursor: "pointer",
                      }}
                    />
                  </div>
                </div>
              </CardComp>

              <div className="row g-4">
                <div className="col-lg-6">
                  {/* Recipients Selection */}
                  <CardComp cardClassName="rounded-4 shadow3 border-0 h-100">
                    <h5 className="fw-bold text-black2 mb-3">Recipients</h5>
                    <p className="text-blacklight mb-3 small">
                      Who should receive birthday messages?
                    </p>
                    <div className="space-y-3">
                      <div className="form-check">
                        <input
                          className="form-check-input"
                          type="radio"
                          name="recipients"
                          id="members_only"
                          value="members"
                          checked={values.recipients === "members"}
                          onChange={() => setFieldValue("recipients", "members")}
                        />
                        <label
                          className="form-check-label pointer"
                          htmlFor="members_only"
                        >
                          Members only
                        </label>
                      </div>
                      <div className="form-check">
                        <input
                          className="form-check-input"
                          type="radio"
                          name="recipients"
                          id="staff_only"
                          value="staff"
                          checked={values.recipients === "staff"}
                          onChange={() => setFieldValue("recipients", "staff")}
                        />
                        <label
                          className="form-check-label pointer"
                          htmlFor="staff_only"
                        >
                          Staff only
                        </label>
                      </div>
                      <div className="form-check">
                        <input
                          className="form-check-input"
                          type="radio"
                          name="recipients"
                          id="both"
                          value="both"
                          checked={values.recipients === "both"}
                          onChange={() => setFieldValue("recipients", "both")}
                        />
                        <label className="form-check-label pointer" htmlFor="both">
                          Both members and staff
                        </label>
                      </div>
                    </div>
                  </CardComp>
                </div>

                <div className="col-lg-6">
                  {/* Communication Channels */}
                  <CardComp cardClassName="rounded-4 shadow3 border-0 h-100">
                    <h5 className="fw-bold text-black2 mb-3">Communication Channels</h5>
                    <p className="text-blacklight mb-3 small">
                      Select how birthday messages should be delivered.
                    </p>
                    <div className="space-y-3">
                      <div className="d-flex align-items-center justify-content-between border-bottom pb-2">
                        <div className="form-check">
                          <input
                            className="form-check-input"
                            type="checkbox"
                            id="channel_email"
                            checked={values.channels.includes("email")}
                            onChange={(e) => {
                              const newChannels = e.target.checked
                                ? [...values.channels, "email"]
                                : values.channels.filter((c) => c !== "email");
                              setFieldValue("channels", newChannels);
                            }}
                          />
                          <label className="form-check-label pointer fw-bold" htmlFor="channel_email">
                            Email
                          </label>
                        </div>
                        <span 
                          className="px-3 py-1 rounded-pill" 
                          style={{ background: "#dafceb", color: "#19bc31", fontSize: "12px", fontWeight: "600" }}
                        >
                          Free
                        </span>
                      </div>

                      <div className="d-flex align-items-center justify-content-between border-bottom pb-2">
                        <div className="form-check">
                          <input
                            className="form-check-input"
                            type="checkbox"
                            id="channel_inapp"
                            checked={values.channels.includes("in-app")}
                            onChange={(e) => {
                              const newChannels = e.target.checked
                                ? [...values.channels, "in-app"]
                                : values.channels.filter((c) => c !== "in-app");
                              setFieldValue("channels", newChannels);
                            }}
                          />
                          <label className="form-check-label pointer fw-bold" htmlFor="channel_inapp">
                            In-App Notification
                          </label>
                        </div>
                        <span 
                          className="px-3 py-1 rounded-pill" 
                          style={{ background: "#dafceb", color: "#19bc31", fontSize: "12px", fontWeight: "600" }}
                        >
                          Free
                        </span>
                      </div>

                      <div className="d-flex align-items-center justify-content-between">
                        <div className="form-check">
                          <input
                            className="form-check-input"
                            type="checkbox"
                            id="channel_sms"
                            checked={values.channels.includes("sms")}
                            onChange={(e) => {
                              const newChannels = e.target.checked
                                ? [...values.channels, "sms"]
                                : values.channels.filter((c) => c !== "sms");
                              setFieldValue("channels", newChannels);
                            }}
                          />
                          <label className="form-check-label pointer fw-bold" htmlFor="channel_sms">
                            SMS
                          </label>
                        </div>
                        <span 
                          className="px-3 py-1 rounded-pill" 
                          style={{ background: "#ffe5e5", color: "#ff4343", fontSize: "12px", fontWeight: "600" }}
                        >
                          Paid
                        </span>
                      </div>
                      <p className="text-blacklight mt-2 x-small italic">
                        * SMS messages are charged based on your current SMS plan.
                      </p>
                    </div>
                  </CardComp>
                </div>
              </div>

              {/* Sending Time */}
              <CardComp cardClassName="rounded-4 shadow3 border-0">
                <h5 className="fw-bold text-black2 mb-3">Sending Time</h5>
                <p className="text-blacklight mb-3 small">
                  When should birthday messages be sent?
                </p>
                <div className="col-lg-4 col-md-6">
                  <TextInputComp
                    type="time"
                    name="sending_time"
                    borderColor="#A1A0A0"
                    placeholder="Select time"
                  />
                  <p className="text-blacklight mt-2 small">
                    Messages will be sent at this time daily
                  </p>
                </div>
              </CardComp>

              <div className="d-flex justify-content-center pt-4">
                <ButtonComp
                  btnText={isUpdating ? "Saving..." : "Save Settings"}
                  btnClassName="bg-primary1 text-white px-5 py-3 rounded-10 border-0 shadow"
                  type="submit"
                  disabled={isUpdating}
                  ButtonInlineStyle={{ minWidth: "250px" }}
                />
              </div>
            </div>
          </Form>
        )}
      </Formik>
    </section>
  );
};

export default BirthdaySettings;

